import { useEffect, useState, useCallback } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { FaUserCircle } from 'react-icons/fa';
import NoProjectsFound from '../../components/NoProjectsFound';
import ProjectCard from '../../components/ProjectCard';
import LoadingIndicator from '../../components/LoadingIndicator';
import Notification from '../../components/Notification';
import api from '../../services/api';
import useInfiniteScroll from '../../hooks/useInfiniteScroll';

import {
  Container,
  DesignerInfo,
  DesignerName,
  TotalProjects,
  ProjectsContainer,
  ProjectsList,
  DesignerImage,
} from './styles';

const ProjectsByDesigner = () => {
  const { designerId } = useParams();
  const location = useLocation();

  const [projects, setProjects] = useState([]);
  const [totalProjects, setTotalProjects] = useState(0);
  const [isLoading, setLoading] = useState(true);
  const [hasNextPage, setHasNextPage] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);

  const fetchMoreProjects = useCallback(async () => {
    if (!hasNextPage) {
      return;
    }

    const nextPage = currentPage + 1;
    try {
      const { data } = await api.get(`/projects_by_designer/${designerId}`, {
        params: { page: nextPage, page_size: 18 },
      });

      setProjects(prev => [...prev, ...data.projects]);
      setCurrentPage(nextPage);

      const totalPages = Math.ceil(data.count / 21);
      setHasNextPage(nextPage < totalPages);
    } catch (err) {
      console.error('Erro ao carregar mais projetos:', err);
      const error = err.response
        ? err.response.data.error
        : 'Erro ao buscar projetos';
      Notification({ message: error, type: Notification.types.ERROR });
    }
  }, [designerId, hasNextPage, currentPage]);

  const [isFetching] = useInfiniteScroll(fetchMoreProjects);

  const fetchInitialProjects = useCallback(async () => {
    try {
      setLoading(true);

      const { data } = await api.get(`/projects_by_designer/${designerId}`, {
        params: { page: 1, page_size: 21 },
      });

      setProjects(data.projects);
      setTotalProjects(data.count);
      setCurrentPage(1);

      const totalPages = Math.ceil(data.count / 21);

      setHasNextPage(1 < totalPages);
    } catch (err) {
      const error = err.response
        ? err.response.data.error
        : 'Erro ao buscar projetos';
      Notification({ message: error, type: Notification.types.ERROR });
    } finally {
      setLoading(false);
    }
  }, [designerId]);

  useEffect(() => {
    fetchInitialProjects();
  }, [fetchInitialProjects]);

  return (
    <Container>
      {isLoading ? (
        <div className="loader" />
      ) : (
        <>
          <DesignerInfo>
            {location.state && location.state.designer.image ? (
              <DesignerImage
                src={location.state.designer.image.url}
                alt="Imagem de usuário"
              />
            ) : (
              <FaUserCircle color="var(--ink-faint)" size={40} />
            )}
            <div>
              <DesignerName>{location.state.designer.name}</DesignerName>
              <TotalProjects>{`${totalProjects} Projetos`}</TotalProjects>
            </div>
          </DesignerInfo>
          <ProjectsContainer>
            {!isLoading && projects.length === 0 ? (
              <NoProjectsFound text="Nenhum projeto encontrado" />
            ) : (
              <>
                <ProjectsList>
                  {projects.map(p => (
                    <ProjectCard project={p} key={p.id} />
                  ))}
                </ProjectsList>
                {isFetching && hasNextPage && (
                  <LoadingIndicator text="Carregando mais projetos..." />
                )}
              </>
            )}
          </ProjectsContainer>
        </>
      )}
    </Container>
  );
};

export default ProjectsByDesigner;
